import React, { useState } from 'react';
import { BookOpen, Calendar, ArrowRight, ArrowLeft, Clock, User } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';

const blogPosts = [
  {
    id: 1,
    image: "https://images.unsplash.com/photo-1612888262725-6b300edf916c?w=800&q=80",
    category: { fr: "Technologie", en: "Technology" },
    date: { fr: "20 janvier 2025", en: "January 20, 2025" },
    readTime: "8 min",
    title: {
      fr: "Découvrez la Puissance de la Modélisation 3D",
      en: "Discover the Power of 3D Modeling"
    },
    excerpt: {
      fr: "Chez D&J Engineering, nous exploitons tout le potentiel de la modélisation 3D pour transformer la manière dont les projets sont conçus et visualisés.",
      en: "At D&J Engineering, we harness the full potential of 3D modeling to transform how residential projects are designed and visualized."
    },
    content: {
      fr: `Chez D&J Engineering, nous exploitons tout le potentiel de la modélisation 3D pour transformer la manière dont les projets sont conçus et visualisés.

La modélisation 3D permet une précision et un réalisme exceptionnels, offrant à nos clients une compréhension claire de chaque élément structurel et de chaque connexion avant même le début des travaux. Que votre projet concerne une construction neuve, une rénovation ou un agrandissement, nos modèles 3D facilitent la visualisation et la planification, tout en réduisant les imprévus sur le chantier.

Chez D&J Engineering, la modélisation 3D est un outil central intégré à nos services de conception, d'estimation et de gestion de projet, garantissant des résultats optimisés, coordonnés et parfaitement adaptés aux besoins de nos clients.

**Visualisation réaliste**
Grâce à la visualisation 3D, vous bénéficiez d'une clarté exceptionnelle et d'une compréhension précise de votre projet avant même le début des travaux. Chaque détail de la structure, des fondations aux poutres et colonnes, est représenté de manière réaliste, vous permettant de visualiser l'interaction entre les différents éléments du bâtiment.

**Meilleure prise de décision**
La visualisation 3D permet d'examiner chaque élément structurel avec précision avant le début des travaux. Cela facilite :
• La sélection des options de conception les plus efficaces.
• L'anticipation des défis techniques et des contraintes sur le chantier.
• Une planification plus stratégique et des décisions éclairées, réduisant les risques de modifications coûteuses.

**Précision et détail**
Chaque composant du bâtiment — des fondations aux poutres, colonnes et charpentes — est représenté avec une précision maximale. Les détails techniques, les connexions et les interactions entre les éléments sont clairement visualisés, garantissant :
• Une conception conforme aux normes canadiennes et locales.
• La réduction des erreurs et des omissions dans les plans et documents.
• Une préparation optimale pour les entrepreneurs et équipes de construction.

**Communication améliorée**
La modélisation 3D facilite la communication entre toutes les parties prenantes :
• Les clients comprennent mieux leur projet grâce à des rendus clairs et réalistes.
• Les architectes et ingénieurs peuvent coordonner plus efficacement les disciplines.
• Les entrepreneurs disposent de documents clairs pour guider l'exécution sur le chantier.

**Flexibilité et innovation**
Grâce aux modèles 3D :
• Les modifications peuvent être intégrées rapidement, sans recommencer tout le plan.
• Les options de conception et de matériaux peuvent être évaluées avant de prendre une décision finale.
• Les nouvelles technologies et méthodologies, comme le BIM, sont facilement intégrées pour optimiser le processus de construction.`,
      en: `At D&J Engineering, we harness the full potential of 3D modeling to transform how residential projects are designed and visualized.

3D modeling provides exceptional precision and realism, giving our clients a clear understanding of every structural element and connection before work even begins. Whether your project involves new construction, renovation, or expansion, our 3D models facilitate visualization and planning while reducing unexpected issues on site.

At D&J Engineering, 3D modeling is a central tool integrated into our design, estimation, and project management services, ensuring optimized, coordinated results perfectly tailored to our clients' needs.

**Realistic Visualization**
With 3D visualization, you benefit from exceptional clarity and precise understanding of your project before work begins. Every detail of the structure, from foundations to beams and columns, is realistically represented, allowing you to visualize the interaction between different building elements.

**Better Decision Making**
3D visualization allows examining each structural element with precision before work begins. This facilitates:
• Selection of the most effective design options.
• Anticipation of technical challenges and site constraints.
• More strategic planning and informed decisions, reducing the risk of costly modifications.

**Precision and Detail**
Every building component — from foundations to beams, columns, and frames — is represented with maximum precision. Technical details, connections, and interactions between elements are clearly visualized, ensuring:
• Design compliant with Canadian and local standards.
• Reduction of errors and omissions in plans and documents.
• Optimal preparation for contractors and construction teams.

**Improved Communication**
3D modeling facilitates communication between all stakeholders:
• Clients better understand their project through clear and realistic renderings.
• Architects and engineers can coordinate disciplines more effectively.
• Contractors have clear documents to guide site execution.

**Flexibility and Innovation**
With 3D models:
• Modifications can be integrated quickly without starting over.
• Design and material options can be evaluated before making a final decision.
• New technologies and methodologies, like BIM, are easily integrated to optimize the construction process.`
    }
  },
  {
    id: 2,
    image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=80",
    category: { fr: "Budget", en: "Budget" },
    date: { fr: "18 janvier 2025", en: "January 18, 2025" },
    readTime: "5 min",
    title: {
      fr: "Pourquoi une estimation précise peut éviter des dépassements budgétaires",
      en: "Why Accurate Estimation Can Prevent Budget Overruns"
    },
    excerpt: {
      fr: "Une estimation détaillée et précise constitue la base de tout projet réussi.",
      en: "A detailed and accurate estimate forms the foundation of any successful project."
    },
    content: {
      fr: `Une estimation détaillée et précise constitue la base de tout projet réussi. En connaissant exactement le coût des matériaux, de la main-d'œuvre et des imprévus, les propriétaires et entrepreneurs peuvent éviter les dépassements budgétaires et planifier efficacement leurs ressources.

Une estimation fiable permet également de comparer plusieurs options et de prendre des décisions éclairées, tout en minimisant le risque de retards ou de dépenses inattendues.

Chez D&J Engineering, nous utilisons des outils modernes comme la modélisation BIM et des bases de données actualisées pour fournir des estimations précises à ±5-10%, vous permettant de planifier votre projet en toute confiance.`,
      en: `A detailed and accurate estimate forms the foundation of any successful project. By knowing exactly the cost of materials, labor, and contingencies, owners and contractors can avoid budget overruns and effectively plan their resources.

A reliable estimate also allows comparing multiple options and making informed decisions, while minimizing the risk of delays or unexpected expenses.

At D&J Engineering, we use modern tools like BIM modeling and updated databases to provide accurate estimates within ±5-10%, allowing you to plan your project with confidence.`
    }
  },
  {
    id: 3,
    image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80",
    category: { fr: "Rénovation", en: "Renovation" },
    date: { fr: "15 janvier 2025", en: "January 15, 2025" },
    readTime: "6 min",
    title: {
      fr: "Comment renforcer une structure existante pour un agrandissement",
      en: "How to Reinforce an Existing Structure for an Extension"
    },
    excerpt: {
      fr: "L'agrandissement ou l'ajout d'un étage nécessite souvent de renforcer la structure existante.",
      en: "Extension or adding a floor often requires reinforcing the existing structure."
    },
    content: {
      fr: `L'agrandissement ou l'ajout d'un étage nécessite souvent de renforcer la structure existante. Cela peut inclure le renforcement des fondations, l'ajout de poutres ou colonnes supplémentaires, et la vérification des murs porteurs.

Une analyse minutieuse par un ingénieur permet de déterminer les modifications nécessaires tout en garantissant la sécurité et la durabilité du bâtiment, avant le début des travaux.

Nos ingénieurs certifiés évaluent chaque projet individuellement pour proposer des solutions optimales qui respectent les normes canadiennes et québécoises.`,
      en: `Extension or adding a floor often requires reinforcing the existing structure. This may include foundation reinforcement, adding additional beams or columns, and verifying load-bearing walls.

A thorough analysis by an engineer determines the necessary modifications while ensuring the safety and durability of the building before work begins.

Our certified engineers evaluate each project individually to propose optimal solutions that comply with Canadian and Quebec standards.`
    }
  },
  {
    id: 4,
    image: "https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=800&q=80",
    category: { fr: "Inspection", en: "Inspection" },
    date: { fr: "12 janvier 2025", en: "January 12, 2025" },
    readTime: "5 min",
    title: {
      fr: "Pourquoi les inspections techniques sont indispensables avant la construction",
      en: "Why Technical Inspections Are Essential Before Construction"
    },
    excerpt: {
      fr: "Les inspections préalables sont essentielles pour détecter des problèmes cachés et valider la conformité des plans.",
      en: "Preliminary inspections are essential to detect hidden problems and validate plan compliance."
    },
    content: {
      fr: `Les inspections préalables sont essentielles pour détecter des problèmes cachés, valider la conformité des plans aux normes et s'assurer que le site est prêt à accueillir la construction.

Elles permettent de prévenir des erreurs coûteuses, de sécuriser le chantier et de garantir que la structure finale respecte toutes les normes locales et provinciales.

Une inspection complète avant le début des travaux est un investissement qui vous protège contre les surprises désagréables et les coûts imprévus.`,
      en: `Preliminary inspections are essential to detect hidden problems, validate plan compliance with standards, and ensure the site is ready for construction.

They help prevent costly errors, secure the site, and ensure the final structure meets all local and provincial standards.

A complete inspection before work begins is an investment that protects you against unpleasant surprises and unexpected costs.`
    }
  },
  {
    id: 5,
    image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80",
    category: { fr: "Innovation", en: "Innovation" },
    date: { fr: "10 janvier 2025", en: "January 10, 2025" },
    readTime: "6 min",
    title: {
      fr: "Les innovations dans le secteur de la construction",
      en: "Innovations in the Residential Construction Sector"
    },
    excerpt: {
      fr: "La construction évolue rapidement grâce à des technologies comme le BIM et la modélisation 3D.",
      en: "Residential construction is evolving rapidly thanks to technologies like BIM and 3D modeling."
    },
    content: {
      fr: `La construction évolue rapidement grâce à des technologies comme le BIM (Building Information Modeling), la modélisation 3D, les matériaux à haute performance et les techniques de préfabrication.

Ces innovations permettent une meilleure précision, une planification optimisée et une réduction des déchets et coûts sur le chantier, tout en augmentant la durabilité et l'efficacité énergétique des bâtiments.

Chez D&J Engineering, nous intégrons ces technologies de pointe dans tous nos projets pour offrir des résultats supérieurs à nos clients.`,
      en: `Residential construction is evolving rapidly thanks to technologies like BIM (Building Information Modeling), 3D modeling, high-performance materials, and prefabrication techniques.

These innovations enable better precision, optimized planning, and reduced waste and costs on site, while increasing building durability and energy efficiency.

At D&J Engineering, we integrate these cutting-edge technologies into all our projects to deliver superior results to our clients.`
    }
  },
  {
    id: 6,
    image: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&q=80",
    category: { fr: "Gestion de projet", en: "Project Management" },
    date: { fr: "8 janvier 2025", en: "January 8, 2025" },
    readTime: "10 min",
    title: {
      fr: "Les étapes de la gestion de projet pour une rénovation ou un agrandissement",
      en: "Project Management Steps for Renovation or Extension"
    },
    excerpt: {
      fr: "Une gestion de projet efficace inclut plusieurs étapes clés pour assurer le succès de votre projet.",
      en: "Effective project management includes several key steps to ensure your project's success."
    },
    content: {
      fr: `Une gestion de projet efficace inclut plusieurs étapes clés :

**1. Analyse initiale et collecte des besoins du client**
Tout projet commence par une compréhension approfondie des objectifs et attentes du client. Nous recueillons toutes les informations nécessaires : plans existants, contraintes techniques, objectifs budgétaires et délais. Cette étape permet de définir clairement l'étendue des travaux et de s'assurer que le projet répond aux besoins réels du client.

**2. Conception et élaboration des plans structuraux**
Une fois les besoins identifiés, notre équipe conçoit des plans structuraux détaillés adaptés au projet. Chaque élément de la structure — fondations, dalles, poutres et colonnes — est conçu pour assurer la sécurité, la durabilité et la conformité aux normes canadiennes et municipales. Nous utilisons également la modélisation 3D pour visualiser chaque détail et optimiser la coordination entre les disciplines.

**3. Estimation des coûts et planification budgétaire**
Avant de passer à l'exécution, nous fournissons une estimation détaillée des coûts incluant matériaux, main-d'œuvre et imprévus. Cette planification budgétaire permet au client de prendre des décisions éclairées et d'éviter les dépassements financiers. Nous analysons également différentes options pour optimiser le budget sans compromettre la qualité ou la sécurité.

**4. Coordination avec architectes, ingénieurs et entrepreneurs**
La collaboration entre toutes les parties prenantes est essentielle pour un projet réussi. Nous assurons la coordination continue entre architectes, ingénieurs, entrepreneurs et fournisseurs afin de garantir que chaque aspect du projet est bien compris et exécuté selon les plans et le calendrier convenu.

**5. Suivi de l'avancement et ajustements nécessaires**
Pendant la construction, nous effectuons un suivi régulier de l'avancement pour identifier les écarts par rapport au plan initial. Si des ajustements sont nécessaires — que ce soit au niveau technique, logistique ou budgétaire — nous les mettons en œuvre rapidement pour maintenir le projet sur la bonne voie.

**6. Vérification finale et livraison du projet**
Enfin, avant la remise officielle, nous réalisons une inspection complète pour nous assurer que tous les travaux sont conformes aux plans et aux normes de qualité. Cette étape garantit une livraison sans surprises et une satisfaction totale du client.

Une gestion structurée réduit les risques de retards et assure un déroulement harmonieux des travaux.`,
      en: `Effective project management includes several key steps:

**1. Initial Analysis and Client Requirements Gathering**
Every project begins with a thorough understanding of the client's objectives and expectations. We gather all necessary information: existing plans, technical constraints, budget objectives, and timelines. This step clearly defines the scope of work and ensures the project meets the client's real needs.

**2. Design and Structural Plan Development**
Once needs are identified, our team designs detailed structural plans tailored to the project. Every structural element — foundations, slabs, beams, and columns — is designed to ensure safety, durability, and compliance with Canadian and municipal standards. We also use 3D modeling to visualize every detail and optimize coordination between disciplines.

**3. Cost Estimation and Budget Planning**
Before execution, we provide a detailed cost estimate including materials, labor, and contingencies. This budget planning allows the client to make informed decisions and avoid financial overruns. We also analyze different options to optimize the budget without compromising quality or safety.

**4. Coordination with Architects, Engineers, and Contractors**
Collaboration between all stakeholders is essential for a successful project. We ensure ongoing coordination between architects, engineers, contractors, and suppliers to guarantee that every aspect of the project is understood and executed according to plans and agreed schedule.

**5. Progress Monitoring and Necessary Adjustments**
During construction, we regularly monitor progress to identify deviations from the initial plan. If adjustments are needed — whether technical, logistical, or budgetary — we implement them quickly to keep the project on track.

**6. Final Verification and Project Delivery**
Finally, before official handover, we conduct a complete inspection to ensure all work complies with plans and quality standards. This step guarantees delivery without surprises and complete client satisfaction.

Structured management reduces the risk of delays and ensures smooth project execution.`
    }
  },
  {
    id: 7,
    image: "https://images.unsplash.com/photo-1589939705384-5185137a7f0f?w=800&q=80",
    category: { fr: "Économie", en: "Economy" },
    date: { fr: "5 janvier 2025", en: "January 5, 2025" },
    readTime: "5 min",
    title: {
      fr: "Techniques modernes pour réduire les coûts sans compromettre la sécurité",
      en: "Modern Techniques to Reduce Costs Without Compromising Safety"
    },
    excerpt: {
      fr: "L'utilisation de matériaux optimisés et de la modélisation 3D permet de réduire les coûts tout en maintenant des standards élevés.",
      en: "Using optimized materials and 3D modeling helps reduce costs while maintaining high standards."
    },
    content: {
      fr: `L'utilisation de matériaux optimisés, la préfabrication d'éléments structurels, la modélisation 3D et la planification précise permettent de réduire les coûts tout en maintenant des standards de sécurité élevés.

La clé est de combiner innovation, précision et contrôle strict à chaque étape du projet.

Nos équipes utilisent les dernières technologies pour optimiser chaque aspect de votre projet, réduisant les gaspillages et maximisant l'efficacité sans jamais compromettre la qualité ou la sécurité de votre construction.`,
      en: `Using optimized materials, prefabrication of structural elements, 3D modeling, and precise planning help reduce costs while maintaining high safety standards.

The key is combining innovation, precision, and strict control at every project stage.

Our teams use the latest technologies to optimize every aspect of your project, reducing waste and maximizing efficiency without ever compromising the quality or safety of your construction.`
    }
  },
  {
    id: 8,
    image: "https://images.unsplash.com/photo-1503387837-b154d5074bd2?w=800&q=80",
    category: { fr: "Conception", en: "Design" },
    date: { fr: "3 janvier 2025", en: "January 3, 2025" },
    readTime: "8 min",
    title: {
      fr: "Étapes clés pour créer un plan de structure pour une maison",
      en: "Key Steps to Create a Structural Plan for a House"
    },
    excerpt: {
      fr: "Créer un plan de structure efficace implique plusieurs étapes essentielles pour garantir la sécurité et la conformité.",
      en: "Creating an effective structural plan involves several essential steps to ensure safety and compliance."
    },
    content: {
      fr: `Créer un plan de structure efficace implique :

**L'analyse du site et des fondations existantes**
Avant toute conception, nous effectuons une étude complète du site et des fondations existantes. Cela inclut l'évaluation du sol, l'identification des contraintes environnementales et la vérification de l'état des structures déjà présentes. Cette analyse permet de déterminer les capacités portantes et les modifications nécessaires pour accueillir la nouvelle structure ou l'agrandissement envisagé.

**La définition des charges et contraintes**
Chaque projet est soumis à des charges spécifiques — poids des matériaux, mobilier, occupants et conditions environnementales comme le vent ou la neige. Nous définissons toutes les contraintes techniques pour garantir que la structure pourra résister à ces charges tout en respectant les normes de sécurité canadiennes et municipales.

**La conception des éléments porteurs (poutres, colonnes, dalles)**
Nous concevons ensuite tous les éléments porteurs, incluant :
• Poutres et solives pour soutenir les planchers et toitures
• Colonnes pour répartir les charges verticales
• Dalles et fondations pour assurer la stabilité de l'ensemble
Chaque élément est optimisé pour combiner sécurité, efficacité et économie de matériaux.

**La modélisation en 3D pour visualiser chaque détail**
La modélisation 3D permet de visualiser le projet de manière réaliste, en représentant toutes les connexions, les points critiques et les interactions entre les éléments de la structure. Cela facilite la détection précoce de conflits potentiels et permet aux clients et entrepreneurs de mieux comprendre le projet avant la construction.

**La vérification par un ingénieur certifié pour conformité aux normes**
Enfin, tous les plans sont révisés et signés par un ingénieur certifié. Cette vérification garantit que la conception est conforme aux normes canadiennes et provinciales, et qu'elle respecte les meilleures pratiques en matière de sécurité et de durabilité.

Ces étapes garantissent des plans fiables et prêts pour l'exécution.`,
      en: `Creating an effective structural plan involves:

**Site and Existing Foundation Analysis**
Before any design, we conduct a complete study of the site and existing foundations. This includes soil evaluation, identifying environmental constraints, and verifying the condition of existing structures. This analysis determines load-bearing capacities and necessary modifications to accommodate the new structure or planned extension.

**Defining Loads and Constraints**
Each project is subject to specific loads — weight of materials, furniture, occupants, and environmental conditions like wind or snow. We define all technical constraints to ensure the structure can withstand these loads while meeting Canadian and municipal safety standards.

**Designing Load-Bearing Elements (Beams, Columns, Slabs)**
We then design all load-bearing elements, including:
• Beams and joists to support floors and roofs
• Columns to distribute vertical loads
• Slabs and foundations to ensure overall stability
Each element is optimized to combine safety, efficiency, and material economy.

**3D Modeling to Visualize Every Detail**
3D modeling allows realistic project visualization, representing all connections, critical points, and interactions between structural elements. This facilitates early detection of potential conflicts and helps clients and contractors better understand the project before construction.

**Verification by Certified Engineer for Standards Compliance**
Finally, all plans are reviewed and signed by a certified engineer. This verification ensures the design complies with Canadian and provincial standards and follows best practices for safety and durability.

These steps ensure reliable plans ready for execution.`
    }
  },
  {
    id: 9,
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80",
    category: { fr: "Agrandissement", en: "Extension" },
    date: { fr: "1 janvier 2025", en: "January 1, 2025" },
    readTime: "7 min",
    title: {
      fr: "Points essentiels à vérifier avant d'agrandir votre maison",
      en: "Essential Points to Check Before Extending Your Home"
    },
    excerpt: {
      fr: "Avant d'agrandir une habitation, plusieurs vérifications cruciales sont nécessaires.",
      en: "Before extending a home, several crucial checks are necessary."
    },
    content: {
      fr: `Avant d'agrandir une habitation, il est crucial de vérifier :

**La capacité des fondations existantes**
Les fondations supportent l'ensemble de la structure. Avant tout agrandissement, il est essentiel de déterminer si elles peuvent supporter le poids supplémentaire. Une étude technique permet de vérifier leur résistance et, si nécessaire, de prévoir des renforcements pour garantir la stabilité de la nouvelle construction.

**L'état des murs porteurs et planchers**
Les murs porteurs et planchers sont les principaux éléments structuraux de votre maison. Il est important de vérifier leur intégrité, de détecter d'éventuelles fissures ou affaissements et d'identifier les zones nécessitant un renforcement avant d'ajouter des charges supplémentaires.

**Les contraintes municipales et régionales**
Les projets d'agrandissement doivent respecter les règlements municipaux et codes de construction locaux. Cela inclut les marges de recul, les hauteurs maximales, les restrictions de zonage et les exigences pour les permis de construction. Une planification en conformité avec ces règles évite les retards et amendes potentielles.

**Les services existants (électricité, plomberie, chauffage)**
Il est également essentiel d'évaluer les installations existantes. Vérifier les systèmes électriques, de plomberie et de chauffage permet d'anticiper les modifications nécessaires pour l'agrandissement et d'éviter des interruptions ou des surcoûts imprévus pendant les travaux.

Une évaluation complète permet de planifier correctement l'agrandissement et d'éviter les surprises.`,
      en: `Before extending a home, it's crucial to check:

**Existing Foundation Capacity**
Foundations support the entire structure. Before any extension, it's essential to determine if they can support additional weight. A technical study verifies their strength and, if necessary, plans reinforcements to ensure the stability of the new construction.

**Condition of Load-Bearing Walls and Floors**
Load-bearing walls and floors are the main structural elements of your home. It's important to verify their integrity, detect any cracks or settling, and identify areas requiring reinforcement before adding additional loads.

**Municipal and Regional Constraints**
Extension projects must comply with municipal regulations and local building codes. This includes setbacks, maximum heights, zoning restrictions, and building permit requirements. Planning in compliance with these rules avoids delays and potential fines.

**Existing Services (Electricity, Plumbing, Heating)**
It's also essential to evaluate existing installations. Checking electrical, plumbing, and heating systems helps anticipate necessary modifications for the extension and avoid interruptions or unexpected costs during work.

A complete evaluation allows proper extension planning and avoids surprises.`
    }
  },
  {
    id: 10,
    image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80",
    category: { fr: "Gestion", en: "Management" },
    date: { fr: "28 décembre 2024", en: "December 28, 2024" },
    readTime: "7 min",
    title: {
      fr: "Comment gérer les modifications en cours de projet",
      en: "How to Manage Changes During a Project"
    },
    excerpt: {
      fr: "Les changements sont fréquents dans les projets de rénovation. Voici comment les gérer efficacement.",
      en: "Changes are common in renovation projects. Here's how to manage them effectively."
    },
    content: {
      fr: `Les changements sont fréquents dans les projets de rénovation et d'agrandissement. Pour les gérer efficacement :

**Documentez chaque modification et son impact sur le plan et le budget**
Chaque changement, qu'il soit mineur ou majeur, doit être documenté avec précision. Cela inclut :
• Les détails techniques de la modification
• L'impact sur les plans existants
• Les conséquences potentielles sur le budget et le calendrier
Une documentation claire permet de garder une traçabilité complète et de prendre des décisions basées sur des informations fiables.

**Validez les changements avec l'ingénieur et l'architecte**
Avant de mettre en œuvre toute modification, il est crucial de consulter les professionnels responsables. Les ingénieurs et architectes doivent évaluer l'impact technique et structurel pour garantir que les changements n'affectent pas la sécurité ou la conformité aux normes. Cette validation permet également de déterminer si des ajustements supplémentaires sont nécessaires.

**Communiquez clairement avec toutes les parties prenantes**
Une communication efficace est la clé pour éviter les malentendus et retards. Informez :
• Les entrepreneurs et sous-traitants des changements à exécuter
• Les clients des impacts sur le budget et le calendrier
• L'équipe de gestion de projet pour coordonner les actions
Un échange clair et structuré assure que tout le monde travaille avec la même compréhension et minimise le risque d'erreurs ou de conflits.

Cette approche assure que le projet reste sur la bonne voie tout en intégrant les ajustements nécessaires.`,
      en: `Changes are common in renovation and extension projects. To manage them effectively:

**Document Each Change and Its Impact on Plan and Budget**
Each change, whether minor or major, must be precisely documented. This includes:
• Technical details of the modification
• Impact on existing plans
• Potential consequences on budget and schedule
Clear documentation maintains complete traceability and enables decisions based on reliable information.

**Validate Changes with Engineer and Architect**
Before implementing any modification, it's crucial to consult responsible professionals. Engineers and architects must evaluate technical and structural impact to ensure changes don't affect safety or standards compliance. This validation also determines if additional adjustments are needed.

**Communicate Clearly with All Stakeholders**
Effective communication is key to avoiding misunderstandings and delays. Inform:
• Contractors and subcontractors of changes to execute
• Clients of impacts on budget and schedule
• Project management team to coordinate actions
Clear and structured exchange ensures everyone works with the same understanding and minimizes the risk of errors or conflicts.

This approach ensures the project stays on track while integrating necessary adjustments.`
    }
  },
  {
    id: 11,
    image: "https://images.unsplash.com/photo-1542621334-a254cf47733d?w=800&q=80",
    category: { fr: "Estimation", en: "Estimation" },
    date: { fr: "25 décembre 2024", en: "December 25, 2024" },
    readTime: "8 min",
    title: {
      fr: "Comment estimer correctement le coût d'un projet",
      en: "How to Correctly Estimate Project Cost"
    },
    excerpt: {
      fr: "Une estimation fiable repose sur plusieurs facteurs clés pour garantir la précision.",
      en: "A reliable estimate relies on several key factors to ensure accuracy."
    },
    content: {
      fr: `Une estimation fiable repose sur :

**La collecte complète des informations sur le projet**
Avant toute estimation, il est essentiel de réunir toutes les informations pertinentes : plans, spécifications, exigences techniques, contraintes du site et besoins du client. Une collecte minutieuse des données permet de comprendre l'ampleur réelle du projet et d'éviter les omissions qui pourraient générer des coûts imprévus.

**L'analyse des quantités de matériaux et de la main-d'œuvre**
Chaque projet nécessite une analyse détaillée des matériaux et de la main-d'œuvre. Cela inclut la quantification exacte des éléments structurels, finitions, équipements et services nécessaires. Cette étape permet de prévoir le coût réel et de planifier efficacement les ressources humaines et matérielles.

**L'intégration des imprévus et des coûts indirects**
Même avec une planification rigoureuse, certains imprévus peuvent survenir : retards, conditions du site, ajustements techniques ou variations de prix. Il est donc essentiel d'inclure une marge pour ces imprévus et d'identifier les coûts indirects liés à la gestion, à la coordination ou aux permis et inspections.

**La mise à jour des prix selon le marché local**
Les prix des matériaux et de la main-d'œuvre varient selon le marché et la saison. Une estimation fiable exige de mettre à jour régulièrement les coûts pour refléter la réalité locale et garantir que le budget reste précis et réaliste.

Des outils modernes comme la modélisation 3D et le BIM permettent d'obtenir des chiffres précis, minimisant les risques financiers et facilitant la prise de décision.`,
      en: `A reliable estimate relies on:

**Complete Project Information Collection**
Before any estimate, it's essential to gather all relevant information: plans, specifications, technical requirements, site constraints, and client needs. Careful data collection helps understand the project's real scope and avoid omissions that could generate unexpected costs.

**Material and Labor Quantity Analysis**
Each project requires detailed analysis of materials and labor. This includes exact quantification of structural elements, finishes, equipment, and necessary services. This step predicts actual cost and effectively plans human and material resources.

**Integration of Contingencies and Indirect Costs**
Even with rigorous planning, unforeseen events can occur: delays, site conditions, technical adjustments, or price variations. It's essential to include a margin for these contingencies and identify indirect costs related to management, coordination, or permits and inspections.

**Price Updates According to Local Market**
Material and labor prices vary by market and season. A reliable estimate requires regular cost updates to reflect local reality and ensure the budget remains accurate and realistic.

Modern tools like 3D modeling and BIM provide accurate figures, minimizing financial risks and facilitating decision-making.`
    }
  }
];

const BlogPage = () => {
  const { language } = useLanguage();
  const [selectedPost, setSelectedPost] = useState(null);

  const getText = (obj) => obj[language] || obj.fr;

  if (selectedPost) {
    const post = blogPosts.find(p => p.id === selectedPost);
    return (
      <div className="min-h-screen bg-white">
        {/* Article Header */}
        <section className="relative h-[50vh] overflow-hidden">
          <img 
            src={post.image} 
            alt={getText(post.title)}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-16">
            <div className="max-w-4xl mx-auto">
              <Button 
                variant="ghost" 
                className="text-white mb-6 hover:bg-white/10"
                onClick={() => setSelectedPost(null)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Retour au blog' : 'Back to blog'}
              </Button>
              <span className="px-3 py-1 rounded-full bg-blue-500 text-white text-sm font-medium">
                {getText(post.category)}
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mt-4 mb-4">
                {getText(post.title)}
              </h1>
              <div className="flex items-center gap-6 text-slate-300">
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {getText(post.date)}
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {post.readTime}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-16">
          <div className="max-w-3xl mx-auto px-6">
            <div className="prose prose-lg prose-slate max-w-none">
              {getText(post.content).split('\n\n').map((paragraph, idx) => {
                if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                  return (
                    <h3 key={idx} className="text-xl font-bold text-slate-900 mt-8 mb-4">
                      {paragraph.replace(/\*\*/g, '')}
                    </h3>
                  );
                }
                if (paragraph.startsWith('**')) {
                  const parts = paragraph.split('**');
                  return (
                    <div key={idx} className="mb-6">
                      <h3 className="text-xl font-bold text-slate-900 mb-3">{parts[1]}</h3>
                      <p className="text-slate-600 leading-relaxed">{parts[2]}</p>
                    </div>
                  );
                }
                if (paragraph.startsWith('•')) {
                  return (
                    <ul key={idx} className="list-disc list-inside text-slate-600 space-y-2 mb-6">
                      {paragraph.split('\n').map((item, i) => (
                        <li key={i}>{item.replace('• ', '')}</li>
                      ))}
                    </ul>
                  );
                }
                return (
                  <p key={idx} className="text-slate-600 leading-relaxed mb-6">
                    {paragraph}
                  </p>
                );
              })}
            </div>

            {/* CTA */}
            <div className="mt-16 p-8 bg-blue-50 rounded-2xl border border-blue-100">
              <h3 className="text-xl font-bold text-slate-900 mb-2">
                {language === 'fr' ? 'Besoin d\'aide pour votre projet?' : 'Need help with your project?'}
              </h3>
              <p className="text-slate-600 mb-4">
                {language === 'fr' 
                  ? 'Contactez nos experts pour une consultation gratuite.'
                  : 'Contact our experts for a free consultation.'}
              </p>
              <Button 
                className="bg-blue-700 hover:bg-blue-800 text-white"
                onClick={() => window.location.href = '/contact'}
              >
                {language === 'fr' ? 'Nous contacter' : 'Contact us'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80" 
            alt="Blog Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-900/85" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <span className="inline-block px-4 py-2 rounded-full bg-blue-500/20 text-blue-300 text-sm font-medium mb-6">
            <BookOpen className="w-4 h-4 inline mr-2" />
            Blog
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            {language === 'fr' ? 'Notre Blog' : 'Our Blog'}
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            {language === 'fr' 
              ? 'Actualités, conseils et guides sur l\'ingénierie structurale et la construction'
              : 'News, tips and guides on structural engineering and construction'}
          </p>
        </div>
      </section>

      {/* Featured Post */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="relative h-80 lg:h-[450px] rounded-2xl overflow-hidden shadow-xl">
              <img 
                src={blogPosts[0].image} 
                alt={getText(blogPosts[0].title)}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
              <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-blue-500 text-white text-sm font-medium">
                {language === 'fr' ? 'À la une' : 'Featured'}
              </span>
            </div>
            <div className="lg:pl-8">
              <span className="px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-sm font-medium">
                {getText(blogPosts[0].category)}
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-4 mb-4">
                {getText(blogPosts[0].title)}
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                {getText(blogPosts[0].excerpt)}
              </p>
              <div className="flex items-center gap-6 text-slate-500 mb-6">
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {getText(blogPosts[0].date)}
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {blogPosts[0].readTime}
                </span>
              </div>
              <Button 
                className="bg-blue-700 hover:bg-blue-800 text-white group"
                onClick={() => setSelectedPost(blogPosts[0].id)}
              >
                {language === 'fr' ? 'Lire l\'article' : 'Read article'}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* All Posts */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-8">
            {language === 'fr' ? 'Tous les articles' : 'All articles'}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.slice(1).map((post) => (
              <Card 
                key={post.id} 
                className="group bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer"
                onClick={() => setSelectedPost(post.id)}
              >
                <CardContent className="p-0">
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={post.image} 
                      alt={getText(post.title)}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium">
                        {getText(post.category)}
                      </span>
                      <span className="flex items-center gap-1 text-slate-500 text-xs">
                        <Clock className="w-3 h-3" />
                        {post.readTime}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-blue-700 transition-colors line-clamp-2">
                      {getText(post.title)}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed mb-4 line-clamp-3">
                      {getText(post.excerpt)}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1 text-slate-500 text-xs">
                        <Calendar className="w-3 h-3" />
                        {getText(post.date)}
                      </span>
                      <span className="text-blue-700 text-sm font-medium group-hover:translate-x-1 transition-transform inline-flex items-center">
                        {language === 'fr' ? 'Lire' : 'Read'}
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
